import clsx from 'clsx'
import React, { ComponentType, useCallback } from 'react'

import DealCard from 'shared-components/deals/DealCard'
import ImagedLink from 'shared-components/ui/ImagedLink'
import Disclaimer from 'shared-components/ui/Disclaimer'
import LoadMoreList from 'shared-components/ui/LoadMoreList'
import PostRow from 'shared-components/posts/PostRow'
import RootCategoryHero from 'shared-components/ui/RootCategoryHero'
import SidebarLayout from 'shared-components/ui/SidebarLayout'

import type {
  DSDeal,
  DSDealsCategoryPage,
  DSResource,
  NCComponentInterface,
  DSPostShortWithAuthor,
} from 'shared-definitions/types'

import styleUtils from 'shared-components/styles/util.module.css'

import styles from './DealsCategoryTemplate.module.css'
import { useCategoryViewed } from '../hooks/use-category-viewed'

type PageLoader<T> = (page: number, perPage: number) => Promise<T>

const mapPageLoader =
  <T, A>(pageLoader: PageLoader<T>, mapper: (data: T) => A): PageLoader<A> =>
  async (page, perPage): Promise<A> => {
    const result = await pageLoader(page, perPage)
    return mapper(result)
  }

interface DealsCategoryTemplateProps<T extends DSResource> {
  page: DSDealsCategoryPage<T>
  pageLoader: PageLoader<null | DSDealsCategoryPage<T>>
  nc: ComponentType<NCComponentInterface<T>>
  leaderBoard: React.ReactElement
  sideBar: React.ReactElement
}

const DealsCategoryTemplate = <T extends DSResource>({
  page,
  pageLoader,
  nc: NC,
  sideBar,
  leaderBoard,
}: DealsCategoryTemplateProps<T>): React.ReactElement => {
  const [hero1, hero2] = page.hero

  return (
    <>
      <div className={styleUtils.wrapper} ref={useCategoryViewed(page.title, page.pagePath, 1)}>
        <Disclaimer />
        <h1 className={clsx(styleUtils.pageTitle, styleUtils.pageTitleMargins)}>
          {page.slots.top.title}
        </h1>
        <div className={styles.dealsGrid}>
          {page.slots.top.hot.map((item, index) => (
            <DealCard key={index} deal={item} variant="hot" />
          ))}
        </div>
        <h3 className={styles.dealsSubTitle}>{page.slots.top.subtitle}</h3>
        <LoadMoreList
          autoload
          className={styles.dealsGrid}
          initial={page.slots.top.deals}
          buttonClassName={styles.loadMore}
          loader={useCallback(
            mapPageLoader(pageLoader, data => data?.slots.top.deals.items ?? []),
            []
          )}
          render={useCallback(
            (item: DSDeal, index) => (
              <DealCard variant="default" key={index} deal={item} />
            ),
            []
          )}
        />
      </div>
      <div className={styleUtils.wrapper}>
        <h1 className={clsx(styleUtils.pageTitle, styles.categoryTitleMargins)}>{page.title}</h1>
        {page.topics ? (
          <div className={styles.categoriesGrid}>
            {page.topics.items.map((item, index) => (
              <ImagedLink key={index} {...item} />
            ))}
          </div>
        ) : null}
        {hero1 ? <RootCategoryHero {...hero1} className={styles.categoryHero} /> : null}
        {hero2 ? (
          <RootCategoryHero
            {...hero2}
            reversed
            className={clsx(styles.categoryHero, styles.categorySecondHero)}
          />
        ) : null}
        {leaderBoard}
        <h2 className={styles.categoryListTitle}>{page.latest.title}</h2>
        <SidebarLayout
          content={
            <LoadMoreList
              autoload
              className={styles.categoryList}
              initial={page.latest.posts}
              loader={useCallback(
                mapPageLoader(pageLoader, data => data?.latest.posts.items ?? []),
                []
              )}
              render={useCallback(
                (item: DSPostShortWithAuthor, index) => (
                  <PostRow key={index} post={item} large />
                ),
                []
              )}
            />
          }
          sidebar={
            <div className={styles.sidebar}>
              {page.sidebarAds.map((item, index) => (
                <NC index={index} key={index} data={item} tpl="sidebar" />
              ))}
              {sideBar}
            </div>
          }
        />
      </div>
    </>
  )
}

export default DealsCategoryTemplate
